/**
 * BackendHealthBanner — shows a non-dismissible banner at the top of the app
 * when the backend is unreachable. Polls `/api/health` (cheap liveness) on
 * mount + every 30 s afterwards. The first reachable response hides the
 * banner; transient blips are tolerated thanks to a small consecutive-fail
 * threshold so users don't see a flicker during ordinary network jitter.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { ServerCrash, RefreshCw } from 'lucide-react';

// Resolve a same-origin API base so the health probe is never CORS-blocked
// when REACT_APP_BACKEND_URL points to a different sub-host than the visited
// page (e.g. `www.credys.co.uk` vs `credys.co.uk`).
function resolveApiBase() {
  const fromEnv = process.env.REACT_APP_BACKEND_URL;
  if (!fromEnv) return `${window.location.origin}/api`;
  try {
    const envHost = new URL(fromEnv).host;
    const envRoot = envHost.replace(/^www\./, '');
    const winRoot = window.location.host.replace(/^www\./, '');
    if (envHost !== window.location.host && envRoot === winRoot) {
      return `${window.location.origin}/api`;
    }
  } catch (_e) { /* fall through */ }
  return `${fromEnv}/api`;
}

const API = resolveApiBase();
const PROBE_INTERVAL_MS = 30_000;
const PROBE_TIMEOUT_MS = 6_000;
// Surface the banner only after this many consecutive failures so a one-off
// hiccup doesn't flash a scary banner at users.
const FAILURE_THRESHOLD = 2;

export default function BackendHealthBanner() {
  const [down, setDown] = useState(false);
  const [reason, setReason] = useState('');
  const [dismissed, setDismissed] = useState(false);
  const failuresRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    let timer = null;

    const probe = async () => {
      try {
        const res = await axios.get(`${API}/health`, {
          timeout: PROBE_TIMEOUT_MS,
          // Cache-bust the URL so a stale 5xx from a CDN/SW can't pin the banner.
          params: { t: Date.now() },
          // We don't send any auth on /health, so never trigger a CORS preflight.
          withCredentials: false,
        });
        if (cancelled) return;
        failuresRef.current = 0;
        setDown(false);
        setReason('');
        // eslint-disable-next-line no-console
        console.debug('[health] OK', res.status, res.data);
      } catch (e) {
        if (cancelled) return;
        failuresRef.current += 1;
        // eslint-disable-next-line no-console
        console.warn('[health] probe failed', {
          count: failuresRef.current,
          code: e?.code,
          message: e?.message,
          status: e?.response?.status,
          url: `${API}/health`,
        });
        if (failuresRef.current >= FAILURE_THRESHOLD) {
          setDown(true);
          // axios "Network Error" usually = browser couldn't reach the host
          // (DNS, CORS preflight failure, server offline). Otherwise we have
          // a real HTTP status from the backend.
          if (e?.response) {
            setReason(`Backend returned HTTP ${e.response.status} from ${API}/health.`);
          } else if (e?.code === 'ECONNABORTED') {
            setReason('Backend is not responding within 6 seconds.');
          } else {
            setReason(`Cannot reach ${API}/health (${e?.message || 'network error'}).`);
          }
        }
      }
    };

    probe();
    timer = setInterval(probe, PROBE_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, []);

  if (!down || dismissed) return null;
  return (
    <div
      className="fixed top-0 inset-x-0 z-[9999] bg-rose-600 text-white shadow-md"
      role="alert"
      data-testid="backend-down-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex flex-wrap items-center gap-3 text-sm">
        <ServerCrash className="w-5 h-5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <strong>Service interruption:</strong>{' '}
          {reason || 'Backend is unreachable from this browser.'}{' '}
          <span className="opacity-90">
            Logins and any data changes won&apos;t go through until this clears.
          </span>
        </div>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="inline-flex items-center gap-1.5 bg-white/15 hover:bg-white/25 px-3 py-1 rounded-md font-semibold transition-colors"
          data-testid="backend-down-reload-btn"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Reload
        </button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/25 px-3 py-1 rounded-md font-semibold transition-colors text-xs"
          data-testid="backend-down-dismiss-btn"
          aria-label="Dismiss banner"
          title="Hide this banner for the rest of the session"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
